import React, { useState } from 'react';
import Header from '../components/layout/Header';
import SideMenu from '../components/layout/SideMenu';
import Alert from '../components/common/Alert';
import Pagination from '../components/common/Pagination';
import LocationFormModal from '../components/products/LocationFormModal';
import SubLocationFormModal from '../components/products/SubLocationFormModal';
import LocationCard from '../components/products/LocationCard';
import SubLocationCard from '../components/products/SubLocationCard';
import ViewToggle from '../components/products/ViewToggle';
import { useLocations } from '../hooks/useLocations';
import styles from './LocationsPage.module.css';
import tableStyles from '../styles/shared/DataTable.module.css';
import cardGridStyles from '../styles/shared/CardGrid.module.css';
import layoutStyles from '../styles/layout.module.css';

const LocationsPage: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const {
    isAdmin,
    activeTab,
    setActiveTab,
    locations,
    subLocations,
    selectedLocationId,
    setSelectedLocationId,
    isLoading,
    isSubmitting,
    viewMode,
    setViewMode,
    isLocationModalOpen,
    isSubLocationModalOpen,
    editingLocation,
    editingSubLocation,
    alert,
    hideAlert,
    handleAddLocation,
    handleEditLocation,
    handleLocationFormSubmit,
    handleAddSubLocation,
    handleEditSubLocation,
    handleSubLocationFormSubmit,
    handleViewSubLocations,
    handleDeleteLocation,
    handleDeleteSubLocation,
    closeLocationModal,
    closeSubLocationModal,
    pagination,
  } = useLocations();

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    pagination.setSearch(searchInput.trim());
    pagination.setPage(1);
  };

  const renderLocationTableView = () => (
    <table className={tableStyles.dataTable}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Coordinates</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {locations.map((location) => (
          <tr key={location.id}>
            <td>{location.location_name}</td>
            <td>{location.long_lat || 'N/A'}</td>
            <td>
              <button onClick={() => handleViewSubLocations(location.id)} className={`${tableStyles.actionButton} ${tableStyles.viewButton}`}>View SubLocations</button>
              <button onClick={() => handleEditLocation(location)} className={`${tableStyles.actionButton} ${tableStyles.editButton}`}>Edit</button>
              <button onClick={() => handleDeleteLocation(location.id)} className={`${tableStyles.actionButton} ${tableStyles.deleteButton}`}>Delete</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderLocationCardView = () => (
    <div className={cardGridStyles.cardsGrid}>
      {locations.map((location) => (
        <LocationCard
          key={location.id}
          location={location}
          onEdit={handleEditLocation}
          onDelete={handleDeleteLocation}
          onViewSubLocations={handleViewSubLocations}
        />
      ))}
    </div>
  );

  const renderSubLocationTableView = () => (
    <table className={tableStyles.dataTable}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Coordinates</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {subLocations.map((subLocation) => (
          <tr key={subLocation.id}>
            <td>{subLocation.sub_location_name}</td>
            <td>{subLocation.long_lat || 'N/A'}</td>
            <td>
              <button onClick={() => handleEditSubLocation(subLocation)} className={`${tableStyles.actionButton} ${tableStyles.editButton}`}>Edit</button>
              <button onClick={() => handleDeleteSubLocation(subLocation.id)} className={`${tableStyles.actionButton} ${tableStyles.deleteButton}`}>Delete</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderSubLocationCardView = () => (
    <div className={cardGridStyles.cardsGrid}>
      {subLocations.map((subLocation) => (
        <SubLocationCard
          key={subLocation.id}
          subLocation={subLocation}
          onEdit={handleEditSubLocation}
          onDelete={handleDeleteSubLocation}
        />
      ))}
    </div>
  );

  if (!isAdmin) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p>Access denied. Admin privileges required.</p>
          </main>
        </div>
      </div>
    );
  }

  if (isLoading && locations.length === 0 && activeTab === 'locations') {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p>Loading locations...</p>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={layoutStyles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <SideMenu />
        <main className={styles.contentArea}>
          <Alert
            message={alert.message}
            type={alert.type}
            isVisible={alert.isVisible}
            onClose={hideAlert}
          />

          <div className={styles.titleBar}>
            <h2 className={styles.title}>Locations Management</h2>
            <div className={styles.controls}>
              <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
              {activeTab === 'locations' ? (
                <button onClick={handleAddLocation} className={styles.addButton}>Add Location</button>
              ) : (
                <button onClick={handleAddSubLocation} className={styles.addButton} disabled={!selectedLocationId}>
                  Add SubLocation
                </button>
              )}
            </div>
          </div>

          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${activeTab === 'locations' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('locations')}
            >
              Locations
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'sublocations' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('sublocations')}
              disabled={locations.length === 0}
            >
              SubLocations
              {selectedLocationId && (
                <span className={styles.locationFilter}>
                  ({locations.find((l) => l.id === selectedLocationId)?.location_name})
                </span>
              )}
            </button>
          </div>

          {activeTab === 'sublocations' && (
            <div className={styles.locationSelector}>
              <label htmlFor="locationSelect">Filter by Location: </label>
              <select
                id="locationSelect"
                value={selectedLocationId || ''}
                onChange={(e) => setSelectedLocationId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Select a location</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.location_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {activeTab === 'locations' && (
            <>
              <div className={styles.toolbar}>
                <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    placeholder="Search locations..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className={styles.searchInput}
                  />
                  <button type="submit" className={styles.searchButton}>Search</button>
                </form>
                <label className={styles.sortLabel}>
                  Sort by:
                  <select
                    value={`${pagination.sortBy}-${pagination.sortOrder}`}
                    onChange={(e) => {
                      const [by, order] = e.target.value.split('-') as [string, 'asc' | 'desc'];
                      pagination.setSortBy(by);
                      pagination.setSortOrder(order);
                      pagination.setPage(1);
                    }}
                    className={styles.sortSelect}
                  >
                    <option value="id-asc">ID (asc)</option>
                    <option value="id-desc">ID (desc)</option>
                    <option value="location_name-asc">Name (A–Z)</option>
                    <option value="location_name-desc">Name (Z–A)</option>
                  </select>
                </label>
              </div>
              {locations.length === 0 && !isLoading && (
                <p>No locations found. Click &quot;Add Location&quot; to create one.</p>
              )}
              {locations.length > 0 && (viewMode === 'table' ? renderLocationTableView() : renderLocationCardView())}
              {activeTab === 'locations' && pagination.total > 0 && (
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  total={pagination.total}
                  limit={pagination.limit}
                  onPageChange={pagination.setPage}
                  onLimitChange={(newLimit) => { pagination.setLimit(newLimit); pagination.setPage(1); }}
                  limitOptions={[10, 20, 50]}
                  showLimitSelector
                  disabled={isLoading}
                />
              )}
            </>
          )}

          {activeTab === 'sublocations' && (
            <>
              {!selectedLocationId && (
                <p>Please select a location to view its sublocations.</p>
              )}
              {selectedLocationId && subLocations.length === 0 && !isLoading && !alert.isVisible && (
                <p>No sublocations found for this location. Click &quot;Add SubLocation&quot; to create one.</p>
              )}
              {selectedLocationId && subLocations.length > 0 && (viewMode === 'table' ? renderSubLocationTableView() : renderSubLocationCardView())}
            </>
          )}

          <LocationFormModal
            isOpen={isLocationModalOpen}
            onClose={closeLocationModal}
            onSubmit={handleLocationFormSubmit}
            initialData={editingLocation}
            isLoading={isSubmitting}
          />

          <SubLocationFormModal
            isOpen={isSubLocationModalOpen}
            onClose={closeSubLocationModal}
            onSubmit={handleSubLocationFormSubmit}
            initialData={editingSubLocation}
            locations={locations}
            isLoading={isSubmitting}
          />
        </main>
      </div>
    </div>
  );
};

export default LocationsPage;
